import { Widget } from '@/components/widget';
import { useState } from 'react';
import { CreateForm } from '@/features/agents/components/create-form';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Dragabble } from '@/components/drag-drop';
import { Button } from '@/components/button';
import { ArrowDownTrayIcon, LinkIcon } from '@heroicons/react/24/outline';
import { RadioGroup } from '@headlessui/react';
import clsx from 'clsx';
import { EditForm } from '@/features/agents/components/edit-form';
import './style.css';
import { useAuthorisedFunction } from '@/utilities/authorised-function';
import { useExportAgent } from '@/features/agents/hooks/use-export-agent';

export interface DefaultValues {
  test?: {
    title?: string;
    sourceExample?: {
      uuid: string;
      container: string;
    };
    inputObject?: string;
    testInput?: string;
    testOutput?: string;
    vaultSchemas?: {
      uuid: string;
      container: string;
      label?: string;
      variableName?: string;
    }[];
    // Runtime status fields for persistence - only the essential ones
    runtimeStatus?: {
      output?: string;
      error?: string;
      log?: string;
    };
  };
  tune?: {
    title?: string;
    tune?: string;
  };
  describe?: {
    title?: string;
    tags?: string;
    description?: string;
    documentation?: string;
    exemplaryInput?: string;
    exemplaryOutput?: string;
    inputSchema?: string;
    outputSchema?: string;
    vaultSchemas?: {
      uuid: string;
      container: string;
    }[];
  };
}

export interface Step {
  id: string;
  href: string;
  name: string;
  status: string;
}

const steps: Step[] = [
  { id: 'create', href: 'create', name: 'Create', status: 'current' },
  { id: 'test', href: ':uuid/test', name: 'Test', status: 'pending' },
  { id: 'tune', href: ':uuid/tune', name: 'Tune', status: 'pending' },
  {
    id: 'describe',
    href: ':uuid/describe',
    name: 'Describe',
    status: 'pending',
  },
];

export function CapabilitiesStudioWidget({ id }: { id: string }) {
  const [, setDefaultValues] = useState<DefaultValues>({});
  const [branch, setBranch] = useState('dev');
  const location = useLocation();
  const currentStep = location.pathname.split('/')?.[3];
  const uuid = location.pathname.split('/')?.[2];
  const { isAuthorised } = useAuthorisedFunction();
  const exportAgent = useExportAgent();
  const hasAgent = !!uuid && currentStep !== undefined && uuid !== 'create';

  return (
    <Widget
      id={id}
      title="Capabilities Studio"
      toolbar={
        <>
          {exportAgent.error ? (
            <span className="text-red-500 text-xs">
              {(exportAgent.error as Error).message}
            </span>
          ) : null}
          {(currentStep === 'test' || currentStep === 'tune') &&
          isAuthorised(['developer', 'fe.agentstudio.developer']) ? (
            <RadioGroup
              className="flex items-center text-xs bg-gray-100 dark:bg-indigo-100/20 rounded-lg p-1"
              value={branch}
              onChange={setBranch}
            >
              <RadioGroup.Option
                value="dev"
                className={({ checked }) =>
                  clsx(
                    'relative flex cursor-pointer rounded-md px-2 py-1 focus:outline-none',
                    checked
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gray-100 dark:bg-transparent text-gray-900 dark:text-gray-100'
                  )
                }
              >
                dev
              </RadioGroup.Option>
              <RadioGroup.Option
                value="main"
                className={({ checked }) =>
                  clsx(
                    'relative flex cursor-pointer rounded-md px-2 py-1 focus:outline-none',
                    checked
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gray-100 dark:bg-transparent text-gray-900 dark:text-gray-100'
                  )
                }
              >
                main
              </RadioGroup.Option>
            </RadioGroup>
          ) : null}
          {hasAgent ? (
            <Button
              title="Download agent with code (dev)"
              aria-label="Download agent with code"
              disabled={exportAgent.isLoading}
              onClick={() => exportAgent.mutate({ agentUuid: uuid })}
            >
              <ArrowDownTrayIcon
                className={clsx(
                  'h-4 w-4',
                  exportAgent.isLoading && 'animate-pulse'
                )}
                aria-hidden="true"
              />
            </Button>
          ) : null}
          <a
            className="inline-flex items-center rounded-md border border-transparent px-2 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-900 dark:text-indigo-100 dark:hover:bg-indigo-700"
            href={`/editor?connectTo=${id}&uuid=${uuid}`}
            target="_blank"
          >
            Editor
          </a>
          <Dragabble value={{ widgetId: id }} type="link">
            <Button>
              <LinkIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </Dragabble>
        </>
      }
      scrollable={false}
    >
      <div>
        <Routes>
          <Route
            index
            path="create"
            element={
              <>
                <CreateForm
                  id={id}
                  onProceed={(data) => {
                    setDefaultValues(data);
                  }}
                  steps={steps}
                />
              </>
            }
          />
          <Route
            path=":uuid/*"
            element={<EditForm id={id} branch={branch} steps={steps} />}
          />
        </Routes>
      </div>
    </Widget>
  );
}
